/**
 * Hotkeys.jsx — Global keyboard shortcut handler + help overlay.
 *
 * Bindings:
 *   Alt+1..9      switch to dashboard N
 *   Alt+N         new dashboard tab
 *   Alt+T         tile windows
 *   Alt+C         cascade windows
 *   Alt+R         restore all windows
 *   Alt+W         close focused window (skips permanent ones)
 *   Alt+M         minimize focused window
 *   ? or F1       toggle this cheatsheet
 *   Esc           close cheatsheet
 *
 * Keys are ignored while the user is typing in an <input>, <textarea>,
 * <select>, or contenteditable element so they never interfere with
 * widget settings forms.
 */

import { createSignal, onCleanup, onMount, Show, For } from 'solid-js';
import wm from '../lib/wmStore.js';
import { EMOJI } from '../lib/emoji.js';
import { homeMenu } from '../lib/homeMenuStore.js';
import './Hotkeys.css';

const BINDINGS = [
  { combo: 'Alt+1…9', desc: 'Switch dashboard' },
  { combo: 'Alt+N',   desc: 'New dashboard tab' },
  { combo: 'Alt+T',   desc: 'Tile windows' },
  { combo: 'Alt+C',   desc: 'Cascade windows' },
  { combo: 'Alt+R',   desc: 'Restore all windows' },
  { combo: 'Alt+W',   desc: 'Close focused window' },
  { combo: 'Alt+M',   desc: 'Minimize focused window' },
  { combo: 'Alt+L',   desc: 'Reset layout to defaults' },
  { combo: '?',       desc: 'Toggle this cheatsheet' },
];

function isTyping(target) {
  if (!target) return false;
  const tag = (target.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (target.isContentEditable) return true;
  return false;
}

function focusedWidgetId() {
  const order = wm.state.focusOrder;
  return order.length ? order[order.length - 1] : null;
}

export default function Hotkeys() {
  const [showHelp, setShowHelp] = createSignal(false);

  function onKey(e) {
    // Help toggles even while typing only via F1; '?' requires no typing.
    if (e.key === 'F1') {
      e.preventDefault();
      setShowHelp(v => !v);
      return;
    }
    if (e.key === 'Escape' && showHelp()) {
      setShowHelp(false);
      return;
    }
    if (isTyping(e.target)) return;

    // #343 — Ctrl+Escape → toggle home menu (NT-style Start-key equivalent).
    if (e.key === 'Escape' && e.ctrlKey && !e.altKey && !e.metaKey) {
      e.preventDefault();
      homeMenu.toggle();
      return;
    }

    if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      setShowHelp(v => !v);
      return;
    }

    if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

    // Alt+digit → switch dashboard
    if (e.key >= '1' && e.key <= '9') {
      const idx = parseInt(e.key, 10) - 1;
      const dash = wm.state.dashboards[idx];
      if (dash) {
        e.preventDefault();
        wm.setActiveDashboard(dash.id);
      }
      return;
    }

    const k = e.key.toLowerCase();
    switch (k) {
      case 't': e.preventDefault(); wm.arrangeTile(); break;
      case 'c': e.preventDefault(); wm.arrangeCascade(); break;
      case 'r': e.preventDefault(); wm.restoreAll(); break;
      case 'n': {
        e.preventDefault();
        if (!wm.state.viewer) return;
        const name = prompt('Dashboard name?', 'New Tab');
        if (name) {
          const emoji = prompt('Emoji?', EMOJI.window) || EMOJI.window;
          wm.addDashboard(name, emoji);
        }
        break;
      }
      case 'w': {
        const id = focusedWidgetId();
        if (!id) return;
        const dash = wm.state.dashboards.find(d => d.id === wm.state.activeDashboardId);
        const w = dash?.widgets.find(x => x.id === id);
        if (w && !w._permanent) {
          e.preventDefault();
          wm.closeWidget(id);
        }
        break;
      }
      case 'm': {
        const id = focusedWidgetId();
        if (id) {
          e.preventDefault();
          wm.toggleMinimize(id);
        }
        break;
      }
      // #343 — Alt+F4: close focused window (NT classic binding).
      // Note: browsers on Windows may intercept Alt+F4 at the OS level;
      // this handler fires in environments where the event reaches JS.
      case 'f4': {
        const id = focusedWidgetId();
        if (!id) return;
        const dash = wm.state.dashboards.find(d => d.id === wm.state.activeDashboardId);
        const w = dash?.widgets.find(x => x.id === id);
        if (w && !w._permanent) {
          e.preventDefault();
          wm.closeWidget(id);
        }
        break;
      }
      case 'l': {
        e.preventDefault();
        if (confirm('Reset this dashboard to its default layout? Your current widgets will be removed.')) {
          wm.resetLayout();
        }
        break;
      }
      default: break;
    }
  }

  onMount(() => window.addEventListener('keydown', onKey));
  onCleanup(() => window.removeEventListener('keydown', onKey));

  return (
    <Show when={showHelp()}>
      <div class="v2-hotkeys-backdrop" onClick={() => setShowHelp(false)}>
        <div class="v2-hotkeys" onClick={(e) => e.stopPropagation()}>
          <header>
            <span>⌨ Keyboard shortcuts</span>
            <button onClick={() => setShowHelp(false)} title="Close (Esc)">✖</button>
          </header>
          <ul>
            <For each={BINDINGS}>{(b) => (
              <li><kbd>{b.combo}</kbd><span>{b.desc}</span></li>
            )}</For>
          </ul>
          <footer>Press <kbd>?</kbd> any time to reopen.</footer>
        </div>
      </div>
    </Show>
  );
}
